function emptyResults(requests) {
  return {
    results: requests.map(() => ({
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page: 0,
      processingTimeMS: 0,
      hitsPerPage: 0,
      exhaustiveNbHits: true,
      query: '',
      params: ''
    }))
  };
}

function hasCustomRequestHandler(config) {
  return config.handleInstantSearchRequests !== undefined;
}

class InstantSearchElasticsearchAdapter {
  constructor(config, requestOptions) {
    this.config = config;
    this.requestOptions = requestOptions;
    this.cache = {};
    if (!hasCustomRequestHandler(this.config) && !this.config.url) {
      throw new Error('Searchkit Instantsearch Client: url is required');
    }
    if (!hasCustomRequestHandler(this.config) && this.requestOptions) {
      throw new Error(
        'Searchkit Instantsearch Client: requestOptions is not supported when used with url. Add the request options to @searchkit/api instead.'
      );
    }
  }

  clearCache() {
    this.cache = {};
    return Promise.resolve(undefined);
  }

  getHeaders() {
    let headers = {};
    if (!hasCustomRequestHandler(this.config) && this.config.headers) {
      headers =
        typeof this.config.headers === 'function'
          ? this.config.headers()
          : this.config.headers;
    }
    return headers;
  }

  async search(instantsearchRequests) {
    try {
      const key = JSON.stringify(instantsearchRequests);
      const cacheValue = this.cache[key];
      if (cacheValue) {
        return cacheValue;
      }

      if (hasCustomRequestHandler(this.config)) {
        const results = await this.config.handleInstantSearchRequests(
          instantsearchRequests,
          this.requestOptions
        );
        this.cache[key] = results;
        return results;
      }

      const response = await fetch(this.config.url, {
        body: JSON.stringify(instantsearchRequests),
        headers: {
          'Content-Type': 'application/json',
          ...this.getHeaders()
        },
        credentials: 'include',
        method: 'POST'
      });

      if (!response.ok) {
        return emptyResults(instantsearchRequests);
      }
      const results = await response.json();
      if (
        results.status === false ||
        !Array.isArray(results.results) ||
        results.results.length === 0
      ) {
        return emptyResults(instantsearchRequests);
      }
      this.cache[key] = results;
      return results;
    } catch {
      return emptyResults(instantsearchRequests);
    }
  }

  async searchForFacetValues(instantsearchRequests) {
    const isr = instantsearchRequests.map((request) => ({
      ...request,
      params: {
        ...request.params,
        hitsPerPage: 0
      }
    }));

    try {
      if (hasCustomRequestHandler(this.config)) {
        const results = await this.config.handleInstantSearchRequests(isr);
        return results.results;
      }

      const response = await fetch(this.config.url, {
        body: JSON.stringify(isr),
        headers: {
          'Content-Type': 'application/json',
          ...this.getHeaders()
        },
        credentials: 'include',
        method: 'POST'
      });

      if (!response.ok) {
        return [];
      }
      const results = await response.json();
      return results.results;
    } catch {
      return [];
    }
  }
}

const createClient = (config, requestOptions) =>
  new InstantSearchElasticsearchAdapter(config, requestOptions);

export default createClient;
